import { useState, useEffect } from "react";
import { MapPin, Package, ArrowsClockwise, Train, Bus, Storefront, Moped, WarningCircle, CheckCircle, Clock, Funnel } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POINT_TYPE_ICONS = {
  bus_station: Bus,
  train_station: Train,
  courier: Moped,
  shop: Storefront,
};

const POINT_TYPE_LABELS = {
  bus_station: "Estacao Autocarro",
  train_station: "Estacao Comboio",
  courier: "Estafeta",
  shop: "Papelaria / Quiosque",
};

function StatWidget({ icon: Icon, value, label, color }) {
  return (
    <div className="bento-item p-5 card-hover" data-testid={`intermediary-stat-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-3">
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={22} weight="duotone" style={{ color }} />
        </div>
      </div>
      <span style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700, color: "var(--text-main)", display: "block" }}>{value}</span>
      <span className="overline">{label}</span>
    </div>
  );
}

function PointCard({ point, isSelected, onSelect }) {
  const TypeIcon = POINT_TYPE_ICONS[point.point_type] || MapPin;
  return (
    <div
      data-testid={`point-card-${point.id}`}
      className="bento-item p-5 cursor-pointer card-hover"
      onClick={() => onSelect(point)}
      style={{ borderColor: isSelected ? "var(--primary)" : "var(--border-color)", borderWidth: isSelected ? 2 : 1 }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--accent-sand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <TypeIcon size={20} weight="duotone" style={{ color: "var(--primary)" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem" }}>{point.name}</h4>
          <span className={`point-badge ${point.point_type}`}>{POINT_TYPE_LABELS[point.point_type]}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-2" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
        <MapPin size={14} /> {point.address}, {point.city}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div style={{ textAlign: "center", padding: "6px 0", borderRadius: 8, background: "var(--bg)" }}>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", color: "var(--primary)", display: "block" }}>{point.active_bags}</span>
          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Bolsas</span>
        </div>
        <div style={{ textAlign: "center", padding: "6px 0", borderRadius: 8, background: "rgba(232,163,89,0.08)" }}>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", color: "var(--warning)", display: "block" }}>{point.pending_pickups}</span>
          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Recolhas</span>
        </div>
        <div style={{ textAlign: "center", padding: "6px 0", borderRadius: 8, background: "rgba(62,102,86,0.08)" }}>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem", color: "var(--success)", display: "block" }}>{point.pending_deliveries}</span>
          <span style={{ fontSize: "0.6rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>Entregas</span>
        </div>
      </div>
    </div>
  );
}

export default function IntermediaryDashboard({ points, bags, shipments, stats, loading }) {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [intermediaryStats, setIntermediaryStats] = useState(null);
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/intermediary/stats`);
        setIntermediaryStats(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchStats();
  }, []);

  const filteredPoints = filterType === "all" ? points : points.filter(p => p.point_type === filterType);

  const pointBags = selectedPoint
    ? bags.filter(b => b.current_location.toLowerCase().includes(selectedPoint.name.toLowerCase().split(" ")[0]))
    : [];

  const pointShipments = selectedPoint
    ? shipments.filter(s => s.pickup_point === selectedPoint.name || s.delivery_point === selectedPoint.name)
    : [];

  return (
    <div data-testid="intermediary-dashboard" className="pt-20 px-6 pb-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <span className="overline" style={{ color: "var(--accent)", display: "block", marginBottom: 4 }}>Dashboard</span>
        <h1 style={{ fontFamily: "Outfit", fontSize: "1.8rem", fontWeight: 700 }}>Pontos de Recolha e Entrega</h1>
        <p style={{ color: "var(--text-muted)", fontSize: "0.9rem" }}>Gestao de intermediarios da rede EcoBolsa</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatWidget icon={MapPin} value={intermediaryStats?.total_points || points.length} label="Total Pontos" color="var(--primary)" />
        <StatWidget icon={Package} value={intermediaryStats?.total_bags_at_points || 0} label="Bolsas nos pontos" color="var(--accent)" />
        <StatWidget icon={ArrowsClockwise} value={intermediaryStats?.total_pending_operations || 0} label="Operacoes pendentes" color="var(--warning)" />
        <StatWidget icon={CheckCircle} value={stats?.delivered || 0} label="Entregues hoje" color="var(--success)" />
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="point-type-filter">
        <Funnel size={16} style={{ color: "var(--text-muted)" }} />
        {[
          { id: "all", label: "Todos" },
          { id: "bus_station", label: "Autocarros" },
          { id: "train_station", label: "Comboios" },
          { id: "courier", label: "Estafetas" },
          { id: "shop", label: "Lojas" },
        ].map(f => (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => setFilterType(f.id)}
            className="nav-item"
            style={{ border: "none", fontFamily: "Manrope", background: filterType === f.id ? "var(--primary)" : "transparent", color: filterType === f.id ? "white" : "var(--text-muted)", fontSize: "0.8rem", padding: "6px 14px" }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Points list */}
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-3" style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto", paddingRight: 4 }}>
            {loading ? (
              <div className="bento-item p-8 text-center"><Clock size={24} style={{ color: "var(--text-muted)", margin: "0 auto" }} /></div>
            ) : filteredPoints.length === 0 ? (
              <div className="bento-item p-8 text-center"><MapPin size={32} style={{ color: "var(--text-muted)", margin: "0 auto" }} /><p style={{ color: "var(--text-muted)", marginTop: 8 }}>Nenhum ponto encontrado</p></div>
            ) : (
              filteredPoints.map(p => (
                <PointCard key={p.id} point={p} isSelected={selectedPoint?.id === p.id} onSelect={setSelectedPoint} />
              ))
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {selectedPoint ? (
            <div className="animate-fade-in" data-testid="point-detail">
              <div className="bento-item p-6 mb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--accent-sand)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {(() => { const Icon = POINT_TYPE_ICONS[selectedPoint.point_type] || MapPin; return <Icon size={24} weight="duotone" style={{ color: "var(--primary)" }} />; })()}
                  </div>
                  <div>
                    <h2 style={{ fontFamily: "Outfit", fontSize: "1.3rem", fontWeight: 700 }}>{selectedPoint.name}</h2>
                    <span className={`point-badge ${selectedPoint.point_type}`}>{POINT_TYPE_LABELS[selectedPoint.point_type]}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4" style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  <MapPin size={16} /> {selectedPoint.address}, {selectedPoint.city}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div style={{ textAlign: "center", padding: 12, borderRadius: 12, background: "var(--bg)" }}>
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", color: "var(--primary)" }}>{selectedPoint.active_bags}</span>
                    <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", marginTop: 2 }}>Bolsas ativas</p>
                  </div>
                  <div style={{ textAlign: "center", padding: 12, borderRadius: 12, background: "rgba(232,163,89,0.08)" }}>
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", color: "var(--warning)" }}>{selectedPoint.pending_pickups}</span>
                    <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", marginTop: 2 }}>Recolhas pendentes</p>
                  </div>
                  <div style={{ textAlign: "center", padding: 12, borderRadius: 12, background: "rgba(62,102,86,0.08)" }}>
                    <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.5rem", color: "var(--success)" }}>{selectedPoint.pending_deliveries}</span>
                    <p style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", marginTop: 2 }}>Entregas pendentes</p>
                  </div>
                </div>
              </div>

              {/* Bags at point */}
              {pointBags.length > 0 && (
                <div className="bento-item p-6 mb-4">
                  <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 12 }}>Bolsas neste ponto</h3>
                  <div className="flex flex-col gap-2">
                    {pointBags.map(bag => (
                      <div key={bag.id} className="flex items-center justify-between" style={{ padding: "10px 14px", borderRadius: 10, background: "var(--bg)" }}>
                        <div className="flex items-center gap-3">
                          <span className="bag-code">{bag.bag_code}</span>
                          <span className={`status-${bag.status}`} style={{ padding: "2px 8px", borderRadius: 9999, fontSize: "0.7rem", fontWeight: 600 }}>
                            {bag.status === "available" ? "Disponivel" : bag.status === "in_transit" ? "Em transito" : bag.status === "at_collection_point" ? "No ponto" : "A aguardar"}
                          </span>
                        </div>
                        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{bag.total_deliveries} entregas</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shipments at point */}
              {pointShipments.length > 0 && (
                <div className="bento-item p-6">
                  <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 12 }}>Encomendas associadas</h3>
                  <div className="flex flex-col gap-2">
                    {pointShipments.map(s => (
                      <div key={s.id} className="flex items-center justify-between" style={{ padding: "10px 14px", borderRadius: 10, background: "var(--bg)" }}>
                        <div className="flex items-center gap-3">
                          <span className="mono" style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--primary)" }}>{s.tracking_code}</span>
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{s.sender_name} → {s.recipient_name}</span>
                        </div>
                        <span className={`status-${s.status}`} style={{ padding: "2px 8px", borderRadius: 9999, fontSize: "0.7rem", fontWeight: 600 }}>
                          {s.status === "in_transit" ? "Em transito" : s.status === "delivered" ? "Entregue" : s.status === "awaiting_pickup" ? "A aguardar" : s.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pointBags.length === 0 && pointShipments.length === 0 && (
                <div className="bento-item p-8 text-center">
                  <WarningCircle size={32} weight="duotone" style={{ color: "var(--text-muted)", margin: "0 auto" }} />
                  <p style={{ color: "var(--text-muted)", marginTop: 8, fontSize: "0.85rem" }}>Sem bolsas ou encomendas associadas neste momento</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bento-item p-12 text-center animate-fade-in">
              <MapPin size={48} weight="duotone" style={{ color: "var(--border-color)", margin: "0 auto 12px" }} />
              <h3 style={{ fontFamily: "Outfit", fontWeight: 600, marginBottom: 4, color: "var(--text-muted)" }}>Selecione um ponto</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Clique num ponto de recolha/entrega para ver detalhes</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
